import { cloneDeep, isEqual, isObject, omit, pick } from 'lodash';
import { v4 as uuid } from 'uuid';
import { util } from 'fabric';
import { unitToPixels } from 'shared/libs/utils/numbers';
import { DefaultQrCode, DefaultTextbox, DefaultImage, DEFAULT_FONT } from 'shared/libs/providers/badge-editor/enum';

/**
 * @typedef {import('fabric').FabricObject} FabricObject
 * @typedef {BadgeEditor.ImageBlock|BadgeEditor.TextBlock|BadgeEditor.QrCodeBlock|BadgeEditor.BackgroundBlock} Block
 * @typedef {typeof DEFAULT_DOC} Doc
 */

export const EMPTY_MODEL_PAGE = {
    blocks: {
        background: {
            width: 812,
            height: 1218,
        },
        text: [],
        qrcode: [],
        images: [],
    },
};


export const DEFAULT_DOC = {
    width: 4,
    height: 6,
    dpi: 203,
    mirrored: false,
    pages: [
        { ...EMPTY_MODEL_PAGE },
    ],
    name: '',
    preset: '4x6_double_sided_tear_resistant_zebra_zd621',
    format: '4x6" Paper badges - Double sided (Zebra ZD621)',
    unit: 'in',
    userUnit: 'in',
};

/** @enum {'single'|'double'|'mirrored'} */
export const MultipageOptions = Object.freeze({
    SINGLE: 'single',
    DOUBLE: 'double',
    MIRRORED: 'mirrored'
});

export const UNIT_LABELS = Object.freeze({
    IN: 'Inches',
    MM: 'Milimeters',
});

/**
 * Removes fields changed because of save
 */
const withoutRev = (data) => structuredClone(omit(data, ['_rev', 'fp_last_changes']));

/**
 *
 * @type {({ width: number, height: number, scaleX?: number, scaleY?: number }) => { width: number, height: number }}
 */
const calculateScaledDimensions = ({ width, height, scaleX, scaleY }) => {
    return {
        width: Math.round(width * (scaleX || 1)),
        height: Math.round(height * (scaleY || 1)),
    };
};

/**
 * Checks if the document has been changed
 * @type {(docToSave: Record<string,unknown>, docInDb: Record<string,unknown>) => boolean}
 */
export const docIsUnChanged = (docToSave, docInDb) => {
    return isEqual(
        withoutRev(docToSave),
        withoutRev(docInDb),
    );
};

/**
 * @param {Record<string,unknown>} object
 * @param {boolean} keepObjectId
 * @param {string[]} allowedFonts
 * @returns {BadgeEditor.Textbox}
 */
export const sanitizeTextObject = (object, keepObjectId, allowedFonts) => {
    // set default values
    /** @type {BadgeEditor.Textbox} */
    const result = {};
    for (const [key, value] of Object.entries(DefaultTextbox)) {
        result[key] = object[key] || value;

        // only keep allowed fonts if defined (in modelToDoc)
        if (key === 'fontFamily' && allowedFonts && !allowedFonts.includes(object.fontFamily)) {
            result[key] = DEFAULT_FONT;
        }
    }

    if (keepObjectId) {
        result._id = object._id;
    }

    // ensure fontSize and minFontSize are rounded
    result.fontSize = Math.round(result.fontSize);
    result.minFontSize = Math.round(result.minFontSize);

    // ensure minFontSize is smaller than fontSize
    if (result.minFontSize > result.fontSize) {
        result.minFontSize = result.fontSize - 1;
    }

    return result;
};

/**
 * @param {Record<string,unknown>} object
 * @param {boolean} keepObjectId
 * @returns {BadgeEditor.QrCode}
 */
export const sanitizeQrCode = (object, keepObjectId) => {
    // set default values
    /** @type {BadgeEditor.QrCode} */
    const result = {};
    for (const [key, value] of Object.entries(DefaultQrCode)) {
        result[key] = object[key] || value;
    }

    if (keepObjectId) {
        result._id = object._id;
    }

    const { width, height } = calculateScaledDimensions(object);
    result.width = width;
    result.height = height;

    result.isQrCode = true;

    return result;
};

/**
 * @param {Record<string,unknown>} object
 * @param {boolean} keepObjectId
 * @returns {BadgeEditor.Image}
 */
export const sanitizeImage = (object, keepObjectId) => {
    /** @type {BadgeEditor.Image} */
    const result = {};

    for (const [key, value] of Object.entries(DefaultImage)) {
        result[key] = object[key] || value;
    }

    if (keepObjectId) {
        result._id = object._id;
    }

    const { width, height } = calculateScaledDimensions(object);
    result.width = width;
    result.height = height;

    result.isImage = true;

    return result;
};

/**
 * @param {BadgeEditor.State} page
 * @param {boolean} [keepObjectId]
 * @param {string[]} [allowedFonts]
 */
export const sanitizePage = ({ blocks }, keepObjectId, allowedFonts) => ({
    blocks: {
        background: { ...(pick(blocks?.background, 'id')) },
        text: (Array.isArray(blocks?.text) ? blocks.text : [])
            .map(text => sanitizeTextObject(text, keepObjectId, allowedFonts)),
        qrcode: (Array.isArray(blocks?.qrcode) ? blocks.qrcode : [])
            .map(qrCode => sanitizeQrCode(qrCode, keepObjectId)),
        images: (Array.isArray(blocks?.images) ? blocks.images : [])
            .map(image => sanitizeImage(image, keepObjectId)),
    },
});

/** @type {(customFonts: Set<string>) => (textbox: BadgeEditor.Textbox) => BadgeEditor.Textbox} */
const createTextboxToDoc = (customFonts) => (textbox) => {
    if (customFonts.has(textbox.fontFamily)) {
        textbox.fontFaceId = textbox.fontFamily;
    }
    delete textbox.fontFamily;

    return textbox;
};

/** @type {(object: FabricObject) => { left: number, top: number }} */
export const calculateObjectCenter = (object) => {
    const radians = util.degreesToRadians(object.angle || 0);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const { width, height, left, top } = object;

    return {
        left: left + (width / 2) * cos - (height / 2) * sin,
        top: top + (width / 2) * sin + (height / 2) * cos
    };
};

/** @type {(object: FabricObject) => FabricObject} */
export const positionToDoc = (object) => {
    const { left, top } = calculateObjectCenter(object);
    const { width, height } = object;

    object.left = left - width / 2;
    object.top = top - height / 2;

    return object;
};

/**
 * Cleans up the model so that it is in the format expected to be saved
 *
 * @param {Record<string, unknown>} model
 * @param {string[]} allowedFonts
 * @param {MultipageOptions} multipageOption
 * @returns {Doc}
 */
export const modelToDoc = (model, allowedFonts, multipageOption) => {
    const doc = cloneDeep(model);
    const customFonts = new Set(allowedFonts.filter(id => id !== DEFAULT_FONT));
    const modifiers = [positionToDoc];

    // sanitize pages
    doc.pages = doc.pages.length
        ? doc.pages.map(page => sanitizePage(page, false, allowedFonts))
        : [sanitizePage(EMPTY_MODEL_PAGE, false, allowedFonts)];

    for (const page of doc.pages) {
        const { background, qrcode, images, text } = page.blocks;
        background.width = unitToPixels(doc.width, doc.dpi, doc.unit);
        background.height = unitToPixels(doc.height, doc.dpi, doc.unit);
        modifyBlocks(
            [qrcode, images, text],
            [modifiers, modifiers, [...modifiers, createTextboxToDoc(customFonts)]]
        );
    }

    // ensure mirrored and pages are correct depending on multipageOptions
    const firstPage = doc.pages.at(0);
    switch (multipageOption) {
        case MultipageOptions.MIRRORED:
            doc.mirrored = true;
            firstPage.mirrored = true;
            doc.pages = [firstPage];
            break;
        case MultipageOptions.DOUBLE:
            doc.mirrored = false;
            delete firstPage?.mirrored;
            break;
        case MultipageOptions.SINGLE:
            doc.mirrored = false;
            doc.pages = [firstPage];
            delete firstPage?.mirrored;
            break;
    }

    return doc;
};

/** @type {(block: Block) => Block} */
const addBlockId = block => {
    if (block && isObject(block)) {
        block._id = uuid();
    }
    return block;
};

/** @type {(blockTypes: Block[][], callbacksPerType: ((object: Block) => Block)[][]) => void} */
export const modifyBlocks = (blockTypes, callbacksPerType) => {
    for (const [index, blocks] of blockTypes.entries()) {
        const callbacks = callbacksPerType?.at(index);
        if (!Array.isArray(blocks) || !Array.isArray(callbacks)) {
            continue;
        }
        for (const object of blocks) {
            for (const callback of callbacks) {
                callback?.(object);
            }
        }
    }
};

/** @type {(textbox: BadgeEditor.Textbox) => BadgeEditor.Textbox} */
const textboxToModel = textbox => {
    textbox.fontFamily = textbox.fontFaceId || textbox.fontFamily || DEFAULT_FONT;
    delete textbox.fontFaceId;

    return textbox;
};

/** @type {(object: FabricObject) => FabricObject} */
export const positionToModel = (object) => {
    const { left, top, width, height, angle } = object;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const radians = util.degreesToRadians(angle || 0);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    object.left = left + halfWidth - halfWidth * cos + halfHeight * sin;
    object.top = top + halfHeight - halfWidth * sin - halfHeight * cos;

    return object;
};

/** @type {(doc: Doc) => Record<string, unknown>} */
export const docToModel = (doc = DEFAULT_DOC) => {
    const model = cloneDeep(doc);
    const modifiers = [addBlockId, positionToModel];

    for (const page of model.pages) {
        const { background, qrcode, images, text } = page.blocks;
        addBlockId(background);
        modifyBlocks(
            [qrcode, images, text],
            [modifiers, modifiers, [...modifiers, textboxToModel]]
        );
    }

    model.userUnit = doc.userUnit || doc.unit;
    return model;
};

/**
 * @param {string} url
 * @returns {Promise<Image>}
 */
export const getImageFromUrl = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => resolve(img);
        img.src = url;
    });

/**
 *
 * @param {BadgeEditor.Textbox|BadgeEditor.Image|BadgeEditor.QrCode} value
 * @returns {'text'|'images'|'qrcode'}
 */
export const getObjectType = (value) => {
    if (value.isQrCode) {
        return 'qrcode';
    }
    if (value.isImage) {
        return 'images';
    }
    return 'text';
};
